// ============================================================================
// EquipesPage — liste des equipes (E1b, F-206..F-212) + DEFERES
// ============================================================================
// Portage du PoC (dashboard)/equipes/page.tsx au standard maison :
//   - onglets Apercu / Forces & profils (F-202/203, deep-link ?tab=forces) ;
//   - Apercu management : grille cartes metier + KPIs + distinctions (M-200) ;
//   - Apercu employee : cartes identite non cliquables (F-204/205, M-201) ;
//   - Forces : agregats k-anonymat ouverts a tous les roles GEST.
// Regle #4 : zero emoji.

import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  PageHeader, EmptyState, GsSegmented, SkeletonCard, QueryError,
} from '@gs/ui';
import type { GsSegmentedOption } from '@gs/ui';
import { Users, ShieldAlert } from 'lucide-react';
import { ApiForbiddenError } from '@gs/api-client';
import { useAuth } from '@gs/auth';
import { useTranslation } from '../../i18n';
import { useMe, isManagementMe } from '../../hooks/useMe';
import {
  useTeams, useTeamsIdentity, useProfilesAnalysis,
} from '../../hooks/useTeams';
import { TeamBadgesAside } from '../../components/equipes/TeamBadgesAside';
import { ForcesProfilsPanel } from '../../components/equipes/ForcesProfilsPanel';
import { TeamIdentityCard } from '../../components/equipes/TeamIdentityCard';
import { TeamOverviewCard } from '../../components/equipes/TeamOverviewCard';
import { EquipesOverviewKpis } from '../../components/equipes/EquipesOverviewKpis';

/** Ecran de refus management (M-200/M-402) — l'autorite est le 403 serveur. */
export function TeamsForbidden() {
  const { t } = useTranslation();
  return (
    <EmptyState
      icon={<ShieldAlert size={48} aria-hidden="true" />}
      title={t('gest:equipes.forbidden.title')}
      description={t('gest:equipes.forbidden.description')}
    />
  );
}

export default function EquipesPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const meQ = useMe(!!user);
  const isManagement = isManagementMe(meQ.data);

  const tab = searchParams.get('tab') === 'forces' ? 'forces' : 'apercu';

  const listQ = useTeams(!!user && isManagement && tab === 'apercu');
  const identityQ = useTeamsIdentity(!!user && !isManagement && tab === 'apercu' && !!meQ.data);
  const analysisQ = useProfilesAnalysis(!!user && tab === 'forces');

  const tabOptions: GsSegmentedOption[] = useMemo(() => [
    { value: 'apercu', label: t('gest:equipes.tabs.apercu') },
    { value: 'forces', label: t('gest:equipes.tabs.forces') },
  ], [t]);

  const setTab = (value: string) => {
    if (value === 'forces') setSearchParams({ tab: 'forces' });
    else setSearchParams({});
  };

  const forbidden =
    (listQ.isError && listQ.error instanceof ApiForbiddenError)
    || (identityQ.isError && identityQ.error instanceof ApiForbiddenError)
    || (analysisQ.isError && analysisQ.error instanceof ApiForbiddenError);

  const refresh = () => {
    if (tab === 'forces') void analysisQ.refetch();
    else if (isManagement) void listQ.refetch();
    else void identityQ.refetch();
  };
  const refreshLoading =
    tab === 'forces' ? analysisQ.isFetching
      : isManagement ? listQ.isFetching
        : identityQ.isFetching;

  return (
    <div className="space-y-5">
      <PageHeader
        title={t('gest:equipes.title')}
        onRefresh={refresh}
        refreshLoading={refreshLoading}
      />

      <GsSegmented
        options={tabOptions}
        value={tab}
        onChange={setTab}
        ariaLabel={t('gest:equipes.tabs.aria')}
      />

      {forbidden ? (
        <TeamsForbidden />
      ) : tab === 'forces' ? (
        analysisQ.isLoading || meQ.isLoading ? (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <SkeletonCard />
            <SkeletonCard />
          </div>
        ) : analysisQ.isError ? (
          <QueryError message={t('gest:equipes.loadError')} onRetry={() => void analysisQ.refetch()} />
        ) : analysisQ.data ? (
          <ForcesProfilsPanel analysis={analysisQ.data.analysis} />
        ) : null
      ) : isManagement ? (
        listQ.isLoading || meQ.isLoading ? (
          <div className="space-y-5">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
            </div>
            <div className="self-start grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
            </div>
          </div>
        ) : listQ.isError ? (
          <QueryError message={t('gest:equipes.loadError')} onRetry={() => void listQ.refetch()} />
        ) : listQ.data ? (
          <>
            <EquipesOverviewKpis kpis={listQ.data.kpis} locale={i18n.language} />

            {listQ.data.teams.length === 0 ? (
              <EmptyState icon={<Users size={48} aria-hidden="true" />} title={t('gest:equipes.empty')} />
            ) : (
              <div className="grid grid-cols-1 gap-5 xl:grid-cols-[1fr_20rem]">
                <div
                  className="grid grid-cols-1 items-stretch gap-4 md:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-3"
                  role="list"
                >
                  {listQ.data.teams.map((team) => (
                    <TeamOverviewCard
                      key={team.id}
                      team={team}
                      to={`/equipes/${team.id}`}
                    />
                  ))}
                </div>
                <TeamBadgesAside teams={listQ.data.teams} currentPeriod={listQ.data.badgePeriod} />
              </div>
            )}
          </>
        ) : null
      ) : identityQ.isLoading || meQ.isLoading ? (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
      ) : identityQ.isError ? (
        <QueryError message={t('gest:equipes.loadError')} onRetry={() => void identityQ.refetch()} />
      ) : identityQ.data ? (
        identityQ.data.teams.length === 0 ? (
          <EmptyState icon={<Users size={48} aria-hidden="true" />} title={t('gest:equipes.empty')} />
        ) : (
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {identityQ.data.teams.map((team) => (
              <TeamIdentityCard key={team.id} team={team} />
            ))}
          </div>
        )
      ) : null}
    </div>
  );
}
